import { doc, onSnapshot } from "https://www.gstatic.com/firebasejs/12.18.0/firebase-firestore.js";
import { STATS_TEXT, validateMarketplaceTotals } from "../modules/marketplace-stats-view.js";

const countElements = {
  clientCount: "marketplaceClientCount",
  providerCount: "marketplaceProviderCount",
  menuItemCount: "marketplaceMenuItemCount"
};
let unsubscribe = null;
let starting = false;
let hasTotals = false;
let locale = document.documentElement.lang || "en";
let lastTotals = null;
let statusKey = "loading";
let failedStatus = false;

function showStatus(key, failed = false) {
  statusKey = key;
  failedStatus = failed;
  document.getElementById("marketplaceStatsStatus").textContent = (STATS_TEXT[locale] || STATS_TEXT.en)[key];
  document.getElementById("marketplaceStatsGrid").setAttribute("aria-busy", "false");
  document.getElementById("marketplaceStatsRetry").classList.toggle("hidden", !failed);
}

function renderTotals(totals, cached = false) {
  validateMarketplaceTotals(totals);
  lastTotals = totals;
  const formatter = new Intl.NumberFormat(locale);
  for (const [key, id] of Object.entries(countElements)) {
    document.getElementById(id).textContent = formatter.format(totals[key]);
  }
  hasTotals = true;
  showStatus(cached ? "cached" : "live");
}

function reportError(error) {
  console.error("[Marketplace Stats]", error);
  showStatus(hasTotals ? "stale" : "error", true);
}

async function startMarketplaceStats() {
  if (starting) return;
  starting = true;
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  showStatus("loading");
  document.getElementById("marketplaceStatsGrid").setAttribute("aria-busy", "true");
  document.getElementById("marketplaceStatsRetry").classList.add("hidden");
  try {
    await window.ensureFirebaseReady();
    const db = window.firebaseDbRaw;
    if (!db) throw new Error("Firestore is not ready.");
    const projectId = window.firebaseConfig && window.firebaseConfig.projectId;
    if (!projectId) throw new Error("Firebase project configuration is missing.");
    const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/getMarketplaceStats`, {
      method: "POST",
      signal: AbortSignal.timeout(30000)
    });
    if (!response.ok) throw new Error(`Public totals request failed (${response.status}).`);
    renderTotals(await response.json());
    unsubscribe = onSnapshot(doc(db, "publicStats", "marketplace"),
      { includeMetadataChanges: true },
      (snapshot) => {
        try {
          if (!snapshot.exists()) throw new Error("Public marketplace totals have not been initialized.");
          renderTotals(snapshot.data(), snapshot.metadata.fromCache);
        } catch (error) {
          reportError(error);
        }
      }, reportError);
  } catch (error) {
    reportError(error);
  } finally {
    starting = false;
  }
}

function initialize() {
  if (!document.getElementById("marketplaceStatsGrid")) return;
  document.getElementById("marketplaceStatsRetry").addEventListener("click", startMarketplaceStats);
  function updateLanguage(language) {
    locale = STATS_TEXT[language] ? language : "en";
    const text = STATS_TEXT[locale];
    for (const [id, key] of Object.entries({
      marketplaceStatsTitle: "title", marketplaceStatsDescription: "description",
      marketplaceClientLabel: "clients", marketplaceProviderLabel: "providers",
      marketplaceMenuItemLabel: "menu", marketplaceStatsRetry: "retry"
    })) document.getElementById(id).textContent = text[key];
    const previousKey = statusKey;
    const previousFailed = failedStatus;
    if (lastTotals) renderTotals(lastTotals);
    showStatus(previousKey, previousFailed);
  }
  document.getElementById("languageSelector").addEventListener("change", (event) => updateLanguage(event.target.value));
  window.addEventListener("languageChanged", (event) => updateLanguage(event.detail.language));
  updateLanguage(document.getElementById("languageSelector").value);
  window.addEventListener("offline", () => showStatus(
    hasTotals ? "offlineSaved" : "offline", true
  ));
  window.addEventListener("online", startMarketplaceStats);
  startMarketplaceStats();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
else initialize();
